// Copyright (c) 2024-present, Dinocollab Technologies, Inc. and its affiliates. All rights reserved.

// imports
import { Box, CircularProgress, Pagination, Typography, colors, styled, alpha, Stack, typographyClasses } from '@mui/material'
// types
import { useMemo, type FC, type ReactNode } from 'react'
import type { BoxProps } from '@mui/material'
import { DataSurfaceViewMode, TRenderableNode } from './types'

const stateWrapBase = {
  padding: '8px',
  height: '100%',
  minHeight: 180,
  width: '100%',
  '& > .inner': {
    width: '100%',
    height: '100%',
    padding: '24px 16px',
    border: `1px dashed ${colors.grey[400]}`,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center'
  }
} as const

export const PanelLoading: FC<BoxProps & { viewMode: DataSurfaceViewMode }> = (props) => {
  const { viewMode, ...rest } = props
  return (
    <PanelLoadingStyled {...rest}>
      <div className='inner'>
        <CircularProgress size={24} />
        <Typography variant='body2' color='primary'>
          Loading...
        </Typography>
      </div>
    </PanelLoadingStyled>
  )
}

const PanelLoadingStyled = styled(Box)({
  ...stateWrapBase,
  '& > div': {
    gap: 8,
    borderColor: colors.blue[400],
    backgroundColor: alpha(colors.blue[50], 0.12)
  }
})

export const PanelError: FC<{ error: string }> = ({ error }) => (
  <PanelErrorStyled>
    <div className='inner'>
      <Typography variant='h6' gutterBottom>
        An error occurred
      </Typography>
      {error && <Typography variant='body2'>{error}</Typography>}
    </div>
  </PanelErrorStyled>
)

const PanelErrorStyled = styled(Box)({
  ...stateWrapBase,
  '& > div': {
    borderColor: colors.red[400],
    backgroundColor: colors.red[50],
    color: colors.red[900],
    flexDirection: 'column'
  }
})

export const PanelNoData: FC = () => (
  <PanelNoDataStyled>
    <div className='inner'>
      <Typography variant='h6' gutterBottom>
        No data available
      </Typography>
      <Typography variant='body2'>Try changing the filter or search keyword</Typography>
    </div>
  </PanelNoDataStyled>
)

const PanelNoDataStyled = styled(Box)({
  ...stateWrapBase,
  '& > div': {
    borderColor: colors.grey[400],
    backgroundColor: colors.grey[50],
    color: colors.grey[700],
    flexDirection: 'column'
  }
})

export const PanelWaiting: FC = () => (
  <PanelWaitingStyled>
    <div className='inner'>
      <Typography variant='h6' color='text.primary' sx={{ fontWeight: 'bold' }}>
        Waiting for data...
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        Please wait while we fetch the data.
      </Typography>
    </div>
  </PanelWaitingStyled>
)

const PanelWaitingStyled = styled(Box)({
  ...stateWrapBase,
  '& > div': {
    borderColor: colors.blue[400],
    backgroundColor: colors.blue[50],
    color: colors.blue[900],
    flexDirection: 'column'
  }
})

export const PanelMissingConfig: FC = () => (
  <PanelMissingConfigStyled>
    <div className='inner'>
      <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
        Missing Configuration
      </Typography>
      <Typography variant='body2'>A required configuration is missing for this view to work correctly.</Typography>
    </div>
  </PanelMissingConfigStyled>
)

const PanelMissingConfigStyled = styled(Box)({
  ...stateWrapBase,
  '& > div': {
    borderColor: colors.red[700],
    backgroundColor: colors.red[50],
    color: colors.red[900],
    flexDirection: 'column'
  }
})

export const PanelLoadMore: FC = () => (
  <PanelLoadMoreStyled>
    <div>
      <CircularProgress size={28} thickness={4} />
      {/* <Typography variant='body2'>Loading more...</Typography> */}
    </div>
  </PanelLoadMoreStyled>
)

const PanelLoadMoreStyled = styled(Box)(({ theme }) => {
  const isDark = theme.palette.mode === 'dark'
  const bgColor = isDark ? 'rgba(0,0,0,0.24)' : 'rgba(255,255,255,0.24)'
  // const textColor = theme.palette.text.primary
  return {
    position: 'absolute',
    inset: 0,
    backgroundColor: bgColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    backdropFilter: 'blur(1.5px)',
    transition: 'opacity 0.2s',
    '& > div': { display: 'flex', alignItems: 'center', gap: 10 }
    // [`.${typographyClasses.root}`]: { color: textColor }
  }
})

export interface IPanelInfiniteScrollFooterSlots {
  statusText?: TRenderableNode
  rangeText?: TRenderableNode
}

export interface IPanelInfiniteScrollFooterProps {
  loading?: boolean
  hasNext?: boolean
  currentCount: number
  total?: number
  slots?: IPanelInfiniteScrollFooterSlots
}

const mapStatusText = (loading?: boolean, hasNext?: boolean): { currentNode: ReactNode; rawText: string } => {
  const mapRawText: Record<string, string> = { loading: 'Loading more...', hasNext: 'Scroll for more', noMore: 'No more items' }
  if (loading) {
    return {
      currentNode: (
        <Stack flexDirection='row' alignItems='center' gap={1}>
          <CircularProgress color='primary' size={12} thickness={3} />
          <Typography variant='caption' color='primary.main'>
            {mapRawText.loading}
          </Typography>
        </Stack>
      ),
      rawText: mapRawText.loading
    }
  } else if (hasNext) {
    return { currentNode: mapRawText.hasNext, rawText: mapRawText.hasNext }
  } else {
    return { currentNode: mapRawText.noMore, rawText: mapRawText.noMore }
  }
}

export const PanelInfiniteScrollFooter: FC<IPanelInfiniteScrollFooterProps> = (props) => {
  const { slots, loading, hasNext, currentCount, total } = props

  const renderText = (value: ReactNode) => {
    if (typeof value !== 'string') return value
    return (
      <Typography variant='caption' color='text.secondary'>
        {value}
      </Typography>
    )
  }

  // Status text on the left
  let obj = mapStatusText(loading, hasNext)
  let finalStatusText: ReactNode = obj.currentNode
  if (typeof slots?.statusText === 'function') {
    finalStatusText = slots.statusText(renderText(obj.currentNode), obj.rawText)
  } else if (slots?.statusText) {
    finalStatusText = slots.statusText
  }

  // Range text on the right
  const rangeText = useMemo(() => {
    if (currentCount <= 0) return '—'
    if (total != null) return `${currentCount} of ${total}`
    if (props.hasNext) return `${currentCount} of many`
    return `${currentCount} results`
  }, [currentCount, total])

  const finalRangeText = typeof slots?.rangeText === 'function' ? slots.rangeText(rangeText, rangeText) : (slots?.rangeText ?? rangeText)

  return (
    <PanelInfiniteScrollFooterStyled>
      <div>
        {renderText(finalStatusText)}
        {renderText(finalRangeText)}
      </div>
    </PanelInfiniteScrollFooterStyled>
  )
}

const PanelInfiniteScrollFooterStyled = styled(Box)(({ theme }) => ({
  '& > div': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
    backgroundColor: alpha(theme.palette.grey[500], 0.08),
    borderTop: `1px solid ${alpha(theme.palette.common.black, 0.08)}`
  },
  [`.${typographyClasses.root}`]: { lineHeight: 1, fontWeight: 500 }
}))

export interface IPanelPaginationFooterProps {
  page: number
  pageSize: number
  total?: number
  hasNext?: boolean
  hasPrev?: boolean
  loading?: boolean
  onPrevPage?: () => void
  onNextPage?: () => void
  onPageJump?: (page: number) => void
}

export const PanelPaginationFooter: FC<IPanelPaginationFooterProps> = (props) => {
  const { page, pageSize, total, hasNext, loading, onPrevPage, onNextPage, onPageJump } = props
  const from = page * pageSize + 1
  const to = total != null ? Math.min(from + pageSize - 1, total) : from + pageSize - 1

  // Calculate page count for MUI Pagination (1-indexed)
  const pageCount = total != null ? Math.ceil(total / pageSize) : undefined

  const handlePageChange = (_event: any, value: number) => {
    // MUI Pagination uses 1-indexed, convert to 0-indexed
    const targetPage = value - 1

    if (onPageJump) {
      // Use direct page jump if available
      onPageJump(targetPage)
    } else {
      // Fallback to prev/next if no jump handler
      if (targetPage < page) {
        onPrevPage?.()
      } else if (targetPage > page) {
        onNextPage?.()
      }
    }
  }

  return (
    <PanelPaginationFooterStyled>
      <Typography variant='caption' color='text.secondary'>
        {total != null ? `${from}–${to} / ${total}` : `Page ${page + 1}`}
      </Typography>
      <Pagination
        count={pageCount}
        page={page + 1}
        onChange={handlePageChange}
        disabled={loading}
        size='small'
        showFirstButton={pageCount != null && pageCount > 5}
        showLastButton={pageCount != null && pageCount > 5}
        siblingCount={0}
        boundaryCount={1}
        hidePrevButton={pageCount == null && !hasNext}
        hideNextButton={pageCount == null && !hasNext}
      />
    </PanelPaginationFooterStyled>
  )
}

const PanelPaginationFooterStyled = styled(Box)(({ theme }) => ({
  padding: '8px 16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`
}))
